// Serializes the inline text editor's contentEditable DOM back into TextRuns.
//
// The editor RENDERS runs as styled <span data-*> grouped into line <div>s (see lineNodes in
// SelectionOverlay), but what we read back is whatever the BROWSER left behind after native
// editing, and that is not always the shape we built. Verified Chrome shapes:
//
//   - typing at the caret: the text lands inside our data-span (kept intact), or as a bare
//     text node next to it;
//   - select-all (Cmd+A) + retype: Chrome deletes everything and rewraps the replacement in
//     SYNTHESIZED inline wrappers carrying none of our data-* markers, e.g.
//     <div><font face=".." color=".."><span style="font-size:.."><b>text</b></span></font></div>;
//   - select-all + Backspace, then type: a bare text node directly under the root, no line
//     divs at all.
//
// So serialization must not assume the built structure: block children are lines, consecutive
// root-level inline content forms a line of its own, and text is collected RECURSIVELY with the
// style inherited from the nearest ancestor span that carries our data-* markers. Text inside
// synthesized wrappers has no markers and becomes a plain run (renders with the layer's base
// style), which keeps a full replacement visible instead of committing an empty layer.

import type { TextRun } from '../types'
import { mergeAdjacentRuns } from './composition-ops'

// Numeric nodeType constants (Node.* globals need a DOM environment; unit tests run without one).
const TEXT_NODE = 3
const ELEMENT_NODE = 1

// Tags the browser uses as line containers inside a contentEditable.
const BLOCK_TAGS = new Set(['DIV', 'P'])

/** Minimal structural view of the DOM nodes we read. `HTMLElement` / `Text` satisfy it, and unit
 *  tests can exercise the exact browser-produced shapes without a DOM environment. */
export type InlineNode = {
  nodeType: number
  nodeName: string
  childNodes?: ArrayLike<InlineNode>
  /** Our style markers on editor-built spans (HTMLElement.dataset). */
  dataset?: Partial<Record<string, string>>
  /** Text node payload (Text.data). */
  data?: string
}

type RunStyle = Pick<
  TextRun,
  | 'color'
  | 'fontSizePct'
  | 'fontWeight'
  | 'fontFamily'
  | 'italic'
  | 'underlineColor'
  | 'highlightColor'
  | 'encircleColor'
>

/** The run style encoded on an editor-built span's data-* markers, or null when it carries none
 *  (browser-synthesized spans, or spans for runs with no per-run overrides). The treatment
 *  markers (it/uc/hc/ec) MUST round-trip, or entering the inline editor silently strips the
 *  underline/highlight/italic/encircle the engine or the user authored. */
function styleFromDataset(el: InlineNode): RunStyle | null {
  const ds = el.dataset
  if (!ds) return null
  const s: RunStyle = {}
  if (ds.c) s.color = ds.c
  if (ds.fs) s.fontSizePct = Number(ds.fs)
  if (ds.fw) s.fontWeight = Number(ds.fw)
  if (ds.ff) s.fontFamily = ds.ff
  if (ds.it) s.italic = true
  if (ds.uc) s.underlineColor = ds.uc
  if (ds.hc) s.highlightColor = ds.hc
  if (ds.ec) s.encircleColor = ds.ec
  return Object.keys(s).length ? s : null
}

/** A newline pasted INSIDE a run is meaningless to us (line structure is carried by separate
 *  runs and the `inline` flag) and actively harmful: both renderers set white-space:pre-wrap, so
 *  a literal \n would paint as a real break and give the run phantom height. Collapse it to a
 *  space, which is what the contentEditable showed while the text was being edited. */
function flattenNewlines(data: string | undefined): string {
  return (data ?? '').replace(/[\r\n]+/g, ' ')
}

/** Depth-first text collection within one line. Any element can wrap the text (our spans, or
 *  Chrome's synthesized <font>/<b>/<span>); style comes from the nearest span with data-*. */
function collectInlineRuns(node: InlineNode, style: RunStyle | null, out: TextRun[]): void {
  if (node.nodeType === TEXT_NODE) {
    const text = flattenNewlines(node.data)
    if (text) out.push(style ? { ...style, text } : { text })
    return
  }
  if (node.nodeType !== ELEMENT_NODE || node.nodeName === 'BR') return
  const next = node.nodeName === 'SPAN' ? (styleFromDataset(node) ?? style) : style
  const kids = node.childNodes
  if (!kids) return
  for (let i = 0; i < kids.length; i++) collectInlineRuns(kids[i], next, out)
}

export function serializeRuns(root: InlineNode): TextRun[] {
  // Group the root's children into lines: each block element is one line; any run of consecutive
  // inline content between/around them (bare text nodes, synthesized wrappers) is a line too.
  const lines: InlineNode[][] = []
  let inlineBuf: InlineNode[] = []
  const flushInline = () => {
    if (inlineBuf.length) {
      lines.push(inlineBuf)
      inlineBuf = []
    }
  }
  const kids = root.childNodes ?? []
  for (let i = 0; i < kids.length; i++) {
    const node = kids[i]
    if (node.nodeType === ELEMENT_NODE && BLOCK_TAGS.has(node.nodeName)) {
      flushInline()
      lines.push([node])
    } else if (node.nodeType === TEXT_NODE || node.nodeType === ELEMENT_NODE) {
      inlineBuf.push(node)
    }
  }
  flushInline()

  const out: TextRun[] = []
  for (const line of lines) {
    let lineRuns: TextRun[] = []
    for (const node of line) collectInlineRuns(node, null, lineRuns)
    if (lineRuns.length === 0) lineRuns = [{ text: '' }] // e.g. a <br>-only (empty) line
    lineRuns = mergeAdjacentRuns(lineRuns)
    lineRuns.forEach((r, i) => out.push(i > 0 ? { ...r, inline: true } : r))
  }
  return out.length ? out : [{ text: '' }]
}
